// Modal de importação de preços. Duas fontes: PDF (leitor OCR reusado do catálogo)
// e CSV (em breve). O PriceTableOcr puxa pdfjs (pesado, incompatível com jsdom),
// então é lazy-load — mesmo padrão do CatalogEditorModal.
//
// Fases do import: 'form' (tabs + grid) → 'importing' (só o card de loading) →
// 'success' (check verde animado + auto-close). O conteúdo do form fica montado
// (hidden) durante importing/success porque o submit roda DENTRO do grid.
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Dropdown } from '@/components/ui/Dropdown';
import { Loader } from '@/components/ui/Loader';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toast';
import { CheckIcon, DownloadIcon } from '@/components/icons';
import {
  useImportPricesFromStoreMutation,
  useListStoresQuery,
  type Store,
} from '../../api';
import styles from './ImportPriceModal.module.css';

const PriceTableOcr = lazy(() =>
  import('@/features/catalog').then((m) => ({ default: m.PriceTableOcr })),
);

const PriceSheetImport = lazy(() =>
  import('@/features/catalog').then((m) => ({ default: m.PriceSheetImport })),
);

// CSV modelo gerado client-side: header + 1 linha por modelo (faixa/lote/m2/fixo).
// BOM UTF-8 + separador `;` pra abrir direto no Excel pt-BR.
const SAMPLE_CSV_ROWS = [
  'categoria;produto;variacao;modelo;qtd_min;qtd_max;area_min_m2;area_max_m2;preco;sku',
  'IMPRESSOS;Cartão de Visita;Couché 300g 4x4;faixa;100;500;;;89,90;',
  'IMPRESSOS;Papel Timbrado;21x29,7cm AP 75grs 1x0;lote;5;;;;430,00;',
  'GRANDE FORMATO;Banner Lona;Lona 440g;m2;;;1;3,99;140,00;',
  'BRINDES;Caneca Personalizada;Cerâmica 325ml;fixo;;;;;25,00;',
];

function downloadSampleCsv() {
  const csv = `\uFEFF${SAMPLE_CSV_ROWS.join('\r\n')}\r\n`;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'modelo-precos.csv';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

type ImportSource = 'pdf' | 'csv' | 'store';
type ImportPhase = 'form' | 'importing' | 'success';

/** Tempo que o check de sucesso fica visível antes do modal fechar sozinho. */
const SUCCESS_CLOSE_MS = 1400;

export interface ImportPriceModalProps {
  open: boolean;
  onClose: () => void;
  /** Loja-alvo fixa do import. Repassada ao grid de revisão. */
  storeId?: string;
  /** Lojas pro Select do grid quando storeId é ausente. */
  stores?: Store[];
}

export function ImportPriceModal({ open, onClose, storeId, stores }: ImportPriceModalProps) {
  const [source, setSource] = useState<ImportSource>('pdf');
  const [phase, setPhase] = useState<ImportPhase>('form');
  const [importedCount, setImportedCount] = useState(0);
  const closeTimer = useRef<number | null>(null);

  // Importar de outra loja (só faz sentido com loja-alvo definida).
  const [sourceStoreId, setSourceStoreId] = useState('');
  const [replace, setReplace] = useState(false);
  const { data: storesPage } = useListStoresQuery(undefined, { skip: !storeId });
  const [importFromStore, { isLoading: importingStore, error: storeErr }] =
    useImportPricesFromStoreMutation();
  const otherStores = (storesPage?.items ?? []).filter((s) => s.id !== storeId);
  const toast = useToast();

  useEffect(() => {
    if (storeErr) toast.danger({ title: 'Não consegui importar.' });
  }, [storeErr, toast]);

  useEffect(
    () => () => {
      if (closeTimer.current != null) window.clearTimeout(closeTimer.current);
    },
    [],
  );

  const handleImportFromStore = async () => {
    if (!storeId || !sourceStoreId) return;
    setPhase('importing');
    try {
      const res = await importFromStore({ storeId, sourceStoreId, replace }).unwrap();
      handleImportSuccess(res.count);
    } catch {
      setPhase('form');
    }
  };

  const handleImportSuccess = (count: number) => {
    setImportedCount(count);
    setPhase('success');
    // A lista de /empresas/precos atrás do modal já refaz o fetch via
    // invalidatesTags — fechar deixa o usuário na página atualizada.
    closeTimer.current = window.setTimeout(onClose, SUCCESS_CLOSE_MS);
  };

  return (
    <Modal open={open} onClose={onClose} title="Importar preços" size="xl">
      <div className={styles.body}>
        {phase !== 'form' && (
          <div className={styles.statusCard} role="status" aria-live="polite">
            {phase === 'importing' ? (
              <>
                <Loader size={56} label="Importando preços" />
                <p className={styles.statusText}>Importando preços…</p>
              </>
            ) : (
              <>
                <span className={styles.successBadge} aria-hidden="true">
                  <CheckIcon size={40} />
                </span>
                <p className={styles.statusText}>
                  {importedCount} {importedCount === 1 ? 'preço importado' : 'preços importados'}
                </p>
              </>
            )}
          </div>
        )}

        <div hidden={phase !== 'form'} className={styles.formArea}>
          <div className={styles.tabs} role="tablist" aria-label="Fonte de importação">
            <button
              type="button"
              role="tab"
              id="import-tab-pdf"
              aria-selected={source === 'pdf'}
              aria-controls="import-panel-pdf"
              className={source === 'pdf' ? styles.tabActive : styles.tab}
              onClick={() => setSource('pdf')}
            >
              PDF
            </button>
            <button
              type="button"
              role="tab"
              id="import-tab-csv"
              aria-selected={source === 'csv'}
              aria-controls="import-panel-csv"
              className={source === 'csv' ? styles.tabActive : styles.tab}
              onClick={() => setSource('csv')}
            >
              CSV
            </button>
            {storeId && (
              <button
                type="button"
                role="tab"
                id="import-tab-store"
                aria-selected={source === 'store'}
                aria-controls="import-panel-store"
                className={source === 'store' ? styles.tabActive : styles.tab}
                onClick={() => setSource('store')}
              >
                Outra loja
              </button>
            )}
          </div>

          {source === 'store' ? (
            <div
              role="tabpanel"
              id="import-panel-store"
              aria-labelledby="import-tab-store"
              className={styles.csv}
            >
              <p className={styles.statusText}>
                Copia a tabela de preços de outra loja da sua empresa para esta loja.
              </p>
              <Dropdown
                aria-label="Loja de origem"
                value={sourceStoreId}
                onChange={(v) => setSourceStoreId(v as string)}
                options={[
                  { value: '', label: 'Selecione a loja de origem…' },
                  ...otherStores.map((s) => ({ value: s.id, label: s.name })),
                ]}
              />
              <label className={styles.csvHeader} style={{ gap: 8 }}>
                <input
                  type="checkbox"
                  checked={replace}
                  onChange={(e) => setReplace(e.target.checked)}
                />
                <span>Substituir os preços atuais desta loja antes de importar</span>
              </label>
              <div className={styles.actions}>
                <Button
                  onClick={() => void handleImportFromStore()}
                  loading={importingStore}
                  disabled={!sourceStoreId}
                >
                  Importar desta loja
                </Button>
              </div>
            </div>
          ) : source === 'pdf' ? (
            <div role="tabpanel" id="import-panel-pdf" aria-labelledby="import-tab-pdf">
              <Suspense
                fallback={
                  <div className={styles.loading}>
                    <Loader label="Carregando leitor de PDF" />
                  </div>
                }
              >
                <PriceTableOcr
                  storeId={storeId}
                  stores={stores}
                  onImportStart={() => setPhase('importing')}
                  onImportSuccess={handleImportSuccess}
                  onImportError={() => setPhase('form')}
                />
              </Suspense>
            </div>
          ) : (
            <div
              role="tabpanel"
              id="import-panel-csv"
              aria-labelledby="import-tab-csv"
              className={styles.csv}
            >
              <div className={styles.csvHeader}>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  leadingIcon={<DownloadIcon size={14} />}
                  onClick={downloadSampleCsv}
                >
                  Baixar modelo
                </Button>
              </div>
              <Suspense
                fallback={
                  <div className={styles.loading}>
                    <Loader label="Carregando importador de planilha" />
                  </div>
                }
              >
                <PriceSheetImport
                  storeId={storeId}
                  stores={stores}
                  onImportStart={() => setPhase('importing')}
                  onImportSuccess={handleImportSuccess}
                  onImportError={() => setPhase('form')}
                />
              </Suspense>
            </div>
          )}

          <div className={styles.actions}>
            <Button variant="ghost" onClick={onClose}>
              Fechar
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
